import "app/components/React/UserSettingDropdown/UserSettingsDropdown.scss";

import type { FC } from "react";
import { useState, useEffect, useRef, useCallback } from "react";

import { useDispatch, useSelector } from "react-redux";

import swLog from "@similarweb/sw-log";
import { Flex } from "@similarweb/ui-components/foundations";

import { appDataSourceSelected } from "app/actions/appIntelligenceDataSourceActions";
import { toggleUserSettings } from "app/actions/userData/primarySidebar3Actions";
import { swSettings } from "app/common/services/swSettings";
import { useAddonsPopover } from "app/components/AddonsSettingsPopover/hooks/useAddonsPopover";
import { ImpersonateModal } from "app/components/impersonate-popup/ImpersonateModal";
import { DEFAULT_HOMEPAGE_PREFERENCE_KEY, DEFAULT_HOMEPAGES } from "app/components/modals/constants";
import { getUiVersion, getUiBranch } from "app/components/ProUIVersionIndication/ProUIVersionIndication";
import ImpersonateAuthCtrl from "app/components/React/ImpersonateAuth/ImpersonateAuthCtrl";
import ImpersonateAuthModal from "app/components/React/ImpersonateAuth/ImpersonateAuthModal";
import { SecondaryActionDropdown } from "app/components/sidebar3/primary-side-bar/secondary-actions/SecondaryActionDropdown";
import { getDropDownContent } from "app/components/sidebar3/primary-side-bar/secondary-actions/UserSettingsItems";
import type { ISecondaryActionWrapper } from "app/components/sidebar3/primary-side-bar/types";
import { i18nFilter } from "app/filters/i18nFilter";
import { AppDataSources } from "app/pages/app-intelligence/common-components/app-solution-toggle/AppSolutionToggle";
import { setShowGetStarted } from "app/pages/digital-suite/home-page/components/ActivationWizard/ActivationWizardApi";
import { useUserInvitation } from "app/pages/digital-suite/home-page/components/InviteUsers/UseUserInvitation";
import { useRewardsModal } from "app/pages/digital-suite/home-page/components/Rewards/useRewardsModal";
import { toggleDevTools } from "app/services/dev-toggle/devToggle";
import { browserUtils } from "app/services/fetchService/browserUtils";
import { openHooksModalV5 } from "app/services/ModalService";
import { PreferencesService } from "app/services/preferences/preferencesService";
import { allTrackers } from "app/services/track/track";
import { getWorkspaceName, isDMITrialOrAfterTrial } from "app/UtilitiesAndConstants/UtilityFunctions/trialUsersUtils";
import { proNavigator } from "single-spa/react-app/ProNavigator";

declare const window;

export const UserSettings: FC<ISecondaryActionWrapper> = ({ onClick, isPrimarySidebarOpen }) => {
    const i18n = i18nFilter();
    const loginUrl = swSettings.swsites.login;
    const {
        CanPurchaseNoTouch: canPurchaseNoTouch,
        CanImpersonate: canImpersonate,
        ClaimsOverwritten: claimsOverwritten,
        ProductsOverwritten: productsOverwritten,
        CanViewSubscriptionProducts: canViewSubscriptionProductsPage,
        CanViewSubscriptionClaims,
        HasLicense: hasLicense,
    } = swSettings.components.Home.resources;

    const impersonateSource = swSettings.user.impersonateSource;
    const impersonateSourceEmail: string = impersonateSource ? impersonateSource.username : "";
    const backToLink = impersonateSource
        ? `${swSettings.swsites.pro}/useridentity/switchidentity?` +
          `name=${encodeURIComponent(impersonateSourceEmail)}&` +
          `userId=${impersonateSource.id}&` +
          `redirectTo=${encodeURIComponent(location.href)}`
        : "";

    const accountDetailsUrl = `${swSettings.swsites.login}/user-details`;
    const subscriptionDetailsUrl = `${swSettings.swsites.login}/subscription-information`;
    const proUrl = swSettings.swsites.pro;
    const supportUrl = `https://support.similarweb.com/hc/en-us/requests/new?ticket_form_id=16743987061533`;
    const supportHomepageUrl = `https://support.similarweb.com/hc/en-us?source=platform_sidebar`;
    const canViewSubscriptionClaimsPage = CanViewSubscriptionClaims;
    const resetClaims = `${swSettings.swsites.login}/api/claims/reset-overrides?redirectTo=${encodeURIComponent(
        location.href,
    )}`;
    const workspace: string = getWorkspaceName();
    const uiVersion = getUiVersion();
    const [showImpersonateAuth, setShowImpersonateAuth] = useState<boolean>(false);
    const [showImpersonateModal, setShowImpersonateModal] = useState<boolean>(false);
    const dropdownRef = useRef<{ closePopup: () => void }>(null);
    const { impersonateMode, userManagementLinksContainer } = useSelector((state) => {
        return {
            impersonateMode: state.impersonation.impersonateMode,
            userManagementLinksContainer: state.common.userManagementLinksContainer,
        };
    });
    const dispatch = useDispatch();

    const openImpersonatePopUp = (): void => {
        setShowImpersonateAuth(true);
        ImpersonateAuthCtrl.shouldAuthenticate()
            .then((res) => {
                if (!res) {
                    setShowImpersonateAuth(false);
                    onImpersonateVerification();
                }
            })
            .catch((err) =>
                swLog.error(
                    `Error on should authenticate check (Okta) while trying to impersonate: ${err.message}`,
                    err,
                ),
            );
    };

    const onImpersonateVerification = (): void => {
        setShowImpersonateAuth(false);
        setShowImpersonateModal(true);
    };

    const onImpersonateClose = (): void => {
        setShowImpersonateAuth(false);
    };

    const logoutClick = (): void => {
        window.location.href = `${loginUrl}/logout`;
    };

    const backTo = (): void => {
        window.location.href = backToLink;
    };

    const onToggleDevTools = (): void => {
        toggleDevTools();
        browserUtils.reloadPage();
    };

    const resetUiVersion = (): void => {
        window.location.href = "/uiversion/";
    };

    const resetUiBranch = () => {
        window.location.href = "/uibranch/reset";
    };

    const onGetStarted = () => {
        allTrackers.dispatchTrackEvent("Side Menu", "click", "Main Sidebar/User Settings/Get Started");

        setShowGetStarted(false);
        proNavigator.go("digitalsuite-home", {}, { location: true, reload: true });
    };

    const onResetDefaultHomepage = (): void => {
        void PreferencesService.remove([DEFAULT_HOMEPAGE_PREFERENCE_KEY]);
        window.location.href = proUrl;
    };

    const { invitationModalComponent, handleOpenModal: inviteTeammate } = useUserInvitation({
        hasAvailableAllocations: true,
    });

    const onOpenContactSales = useCallback(() => {
        dropdownRef.current?.closePopup();
        allTrackers.dispatchTrackEvent("Sidebar Interaction", "click", "settings and help/contact sales/sidebar menu");
        void openHooksModalV5("ContactUs", undefined, "ContactUsMenu", "GetLoading");
    }, []);
    const { rewardsModalComponent, handleOpenModal: openRewards } = useRewardsModal();
    const isDMITrialAfterTrial = isDMITrialOrAfterTrial();

    const currentHomepage = PreferencesService.get(DEFAULT_HOMEPAGE_PREFERENCE_KEY);
    const shouldDisplayResetHomepage =
        currentHomepage && currentHomepage !== DEFAULT_HOMEPAGES.DS && swSettings.user.isInternalUser;

    const NewDropdownContent = getDropDownContent({
        canPurchaseNoTouch,
        subscriptionDetailsUrl,
        accountDetailsUrl,
        workspace,
        impersonateMode,
        canImpersonate,
        hasLicense,
        toggleDevTools: onToggleDevTools,
        claimsOverwritten,
        productsOverwritten,
        canViewSubscriptionClaimsPage,
        swSettings,
        userManagementLinksContainer,
        resetClaims,
        supportUrl,
        supportHomepageUrl,
        logoutClick,
        inviteTeammate,
        openRewards,
        backTo,
        impersonateSourceEmail,
        canViewSubscriptionProductsPage,
        version: uiVersion,
        branch: getUiBranch(),
        resetUiVersion,
        resetUiBranch,
        openImpersonatePopUp,
        resetAppsAddon: () => dispatch(appDataSourceSelected(AppDataSources.PREMIUM_PLUS)),
        onGetStarted,
        isDMITrialAfterTrial,
        onResetDefaultHomepage,
        shouldDisplayResetHomepage,
        onOpenContactSalesModal: onOpenContactSales,
    });

    useEffect(() => {
        dispatch(toggleUserSettings(false));
    }, [dispatch]);

    const handleImpersonateClose = useCallback(() => {
        setShowImpersonateModal(false);
    }, [setShowImpersonateModal]);

    const popoverContainerRef = useRef<HTMLDivElement>(null);

    const { onClosePopover, renderAddonPopover } = useAddonsPopover();

    return (
        <>
            <Flex ref={popoverContainerRef}>
                <SecondaryActionDropdown
                    title={i18n("usersettingsdropdown.settings")}
                    iconName={"sidebar3-settings-help"}
                    onClick={() => {
                        onClosePopover();
                        onClick();
                    }}
                    isPrimarySidebarOpen={isPrimarySidebarOpen}
                    content={NewDropdownContent}
                    dropdownRef={dropdownRef}
                />
            </Flex>

            {renderAddonPopover(popoverContainerRef)}

            {showImpersonateAuth ? (
                <ImpersonateAuthModal
                    onVerify={onImpersonateVerification}
                    onClose={onImpersonateClose}
                />
            ) : null}
            <ImpersonateModal
                isOpen={showImpersonateModal}
                onClose={handleImpersonateClose}
            />
            {invitationModalComponent}
            {rewardsModalComponent}
        </>
    );
};
